// frontend/src/components/Modals/BulkVideoWatermarkModal/BulkVideoWatermarkModal.js
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import './BulkVideoWatermarkModal.css';
import { API_URL as CONFIG_API_URL, API_BASE_URL_EXPORT, BASE_URL } from '../../../config';

function BulkVideoWatermarkModal({ isOpen, onClose, selectedContents, API_URL, onSuccess }) {
    const [watermarkFile, setWatermarkFile] = useState(null);
    const [watermarkPreview, setWatermarkPreview] = useState(null);
    const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
    const [watermarkSize, setWatermarkSize] = useState(100);
    const [watermarkOpacity, setWatermarkOpacity] = useState(80);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processProgress, setProcessProgress] = useState({ current: 0, total: 0, currentTitle: '' });
    const [processResults, setProcessResults] = useState([]);
    const [activeVideoIndex, setActiveVideoIndex] = useState(0);

    // Markalar için state
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [selectedBrandLogo, setSelectedBrandLogo] = useState(null);

    const iconInputRef = useRef(null);

    // Markaları yükle
    useEffect(() => {
        if (isOpen) {
            fetchBrands();
        }
    }, [isOpen]);

    const fetchBrands = async () => {
        try {
            setLoadingBrands(true);
            const response = await axios.get(`${CONFIG_API_URL}/brands`);
            const activeBrands = (response.data.brands || []).filter(
                brand => brand.is_active && (brand.logo_dark_url || brand.logo_light_url)
            );
            setBrands(activeBrands);
        } catch (error) {
            console.error('Brands fetch error:', error);
        } finally {
            setLoadingBrands(false);
        }
    };

    // ESC tuşu
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27 && isOpen && !isProcessing) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose, isProcessing]);

    // Modal kapandığında temizle
    useEffect(() => {
        if (!isOpen) {
            setWatermarkFile(null);
            setWatermarkPreview(null);
            setWatermarkPosition('bottom-right');
            setWatermarkSize(100);
            setWatermarkOpacity(80);
            setIsProcessing(false);
            setProcessProgress({ current: 0, total: 0, currentTitle: '' });
            setProcessResults([]);
            setActiveVideoIndex(0);
            setSelectedBrandLogo(null);
        }
    }, [isOpen]);

    // Video içerikleri filtrele
    const videoContents = selectedContents.filter(content => {
        if (!content.media_path) return false;
        try {
            const mediaType = content.media_type;
            if (mediaType) {
                const types = JSON.parse(mediaType);
                return types.some(t => t === 'video');
            }
            return false;
        } catch {
            return content.media_type === 'video';
        }
    });

    // Logo seç
    const handleIconSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Sadece resim dosyaları kullanılabilir');
                e.target.value = null;
                return;
            }

            setWatermarkFile(file);
            setSelectedBrandLogo(null);

            const reader = new FileReader();
            reader.onloadend = () => {
                setWatermarkPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Marka logosu seçildiğinde
    const handleBrandLogoSelect = async (brand, logoType) => {
        const logoUrl = logoType === 'dark' ? brand.logo_dark_url : brand.logo_light_url;
        if (!logoUrl) return;

        try {
            const fullUrl = `${BASE_URL}${logoUrl}`;

            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const fileName = logoUrl.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });

            setWatermarkFile(file);
            setWatermarkPreview(fullUrl);
            setSelectedBrandLogo({ brandId: brand.id, type: logoType });
            // Markanın varsayılan watermark ayarlarını uygula
            if (brand.default_wm_position) setWatermarkPosition(brand.default_wm_position);
            if (brand.default_wm_size) setWatermarkSize(brand.default_wm_size);
            if (brand.default_wm_opacity) setWatermarkOpacity(brand.default_wm_opacity);

            if (iconInputRef.current) {
                iconInputRef.current.value = null;
            }
        } catch (error) {
            console.error('Brand logo select error:', error);
            toast.error('Logo yüklenirken hata oluştu');
        }
    };

    // Logo kaldır
    const handleRemoveIcon = () => {
        setWatermarkFile(null);
        setWatermarkPreview(null);
        setSelectedBrandLogo(null);
        if (iconInputRef.current) {
            iconInputRef.current.value = null;
        }
    };

    // Toplu işlemi başlat
    const handleStartBulkProcess = async () => {
        if (!watermarkFile) {
            toast.error('Önce bir logo seçmelisiniz');
            return;
        }

        if (videoContents.length === 0) {
            toast.error('İşlenecek video bulunamadı');
            return;
        }

        setIsProcessing(true);
        setProcessProgress({ current: 0, total: videoContents.length, currentTitle: '' });
        setProcessResults([]);

        const results = [];
        const token = localStorage.getItem('token');

        for (let i = 0; i < videoContents.length; i++) {
            const content = videoContents[i];
            setProcessProgress({
                current: i + 1,
                total: videoContents.length,
                currentTitle: content.title
            });

            try {
                const formData = new FormData();
                formData.append('watermark_logo', watermarkFile);
                formData.append('position', watermarkPosition);
                formData.append('size', watermarkSize);
                formData.append('opacity', watermarkOpacity);

                const response = await fetch(`${CONFIG_API_URL}/content-library/${content.id}/add-video-watermark`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });

                const data = await response.json();

                if (data.success) {
                    results.push({
                        id: content.id,
                        title: content.title,
                        success: true,
                        message: 'Logo eklendi'
                    });
                } else {
                    results.push({
                        id: content.id,
                        title: content.title,
                        success: false,
                        message: data.error || 'Hata oluştu'
                    });
                }
            } catch (error) {
                console.error(`İçerik ${content.id} işlenirken hata:`, error);
                results.push({
                    id: content.id,
                    title: content.title,
                    success: false,
                    message: error.message || 'Bağlantı hatası'
                });
            }

            setProcessResults([...results]);

            if (i < videoContents.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        setIsProcessing(false);

        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        if (successCount > 0 && failCount === 0) {
            toast.success(`${successCount} video başarıyla işlendi!`);
            setTimeout(() => onSuccess(), 1500);
        } else if (successCount > 0) {
            toast.success(`${successCount} başarılı, ${failCount} başarısız`);
        } else {
            toast.error('Tüm işlemler başarısız oldu');
        }
    };

    // Video URL'ini al
    const getVideoUrl = (content) => {
        try {
            if (content.media_path) {
                let paths;
                let types;

                try {
                    paths = JSON.parse(content.media_path);
                } catch {
                    paths = [content.media_path];
                }

                try {
                    types = JSON.parse(content.media_type);
                } catch {
                    types = [content.media_type];
                }

                const videoIndex = types.findIndex(t => t === 'video');
                const filename = videoIndex !== -1 ? paths[videoIndex] : paths[0];

                return `${API_BASE_URL_EXPORT}/uploads/${filename}`;
            }
        } catch (e) {
            console.error('getVideoUrl error:', e);
        }
        return null;
    };

    if (!isOpen) return null;

    // Pozisyonlar
    const positions = [
        { key: 'top-left', label: 'Sol Üst' },
        { key: 'top-center', label: 'Orta Üst' },
        { key: 'top-right', label: 'Sağ Üst' },
        { key: 'center-left', label: 'Sol Orta' },
        { key: 'center', label: 'Orta' },
        { key: 'center-right', label: 'Sağ Orta' },
        { key: 'bottom-left', label: 'Sol Alt' },
        { key: 'bottom-center', label: 'Orta Alt' },
        { key: 'bottom-right', label: 'Sağ Alt' }
    ];

    const currentPositionLabel = positions.find(p => p.key === watermarkPosition)?.label || 'Sağ Alt';

    return (
        <div className="bvw-modal-overlay" onClick={() => !isProcessing && onClose()}>
            <div className="bvw-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bvw-modal-header">
                    <div className="bvw-header-title">
                        <span className="bvw-header-icon">🎬</span>
                        <h3>Toplu Video Logo Ekleme</h3>
                        <span className="bvw-video-count">{videoContents.length} video</span>
                    </div>
                    {!isProcessing && (
                        <button className="bvw-close-btn" onClick={onClose}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="bvw-modal-content">
                    {!isProcessing ? (
                        <>
                            {/* Sol Panel - Video Listesi */}
                            <div className="bvw-video-list-panel">
                                <div className="bvw-panel-title">
                                    <span>📹</span> Seçili Videolar
                                </div>
                                <div className="bvw-video-list">
                                    {videoContents.map((content, index) => (
                                        <div
                                            key={content.id}
                                            className={`bvw-video-item ${activeVideoIndex === index ? 'active' : ''}`}
                                            onClick={() => setActiveVideoIndex(index)}
                                        >
                                            <div className="bvw-video-thumb">
                                                <video
                                                    src={getVideoUrl(content)}
                                                    muted
                                                    preload="metadata"
                                                    onLoadedMetadata={(e) => {
                                                        e.target.currentTime = 1;
                                                    }}
                                                />
                                                <div className="bvw-thumb-overlay">▶</div>
                                            </div>
                                            <div className="bvw-video-info">
                                                <span className="bvw-video-title">{content.title}</span>
                                                <span className="bvw-video-meta">
                                                    {content.content_tags?.slice(0, 2).map(t => t.name).join(', ') || 'Etiketsiz'}
                                                </span>
                                            </div>
                                            <div className="bvw-video-index">#{index + 1}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Orta Panel - Önizleme */}
                            <div className="bvw-preview-panel">
                                <div className="bvw-preview-title">Önizleme</div>
                                <div className="bvw-preview-container">
                                    {videoContents[activeVideoIndex] && (
                                        <div className="bvw-preview-wrapper">
                                            <video
                                                src={getVideoUrl(videoContents[activeVideoIndex])}
                                                controls
                                                className="bvw-preview-video"
                                            />
                                            {watermarkPreview && (
                                                <div
                                                    className={`bvw-logo-overlay bvw-pos-${watermarkPosition}`}
                                                    style={{ opacity: watermarkOpacity / 100 }}
                                                >
                                                    <img
                                                        src={watermarkPreview}
                                                        alt="Logo"
                                                        style={{ height: `${Math.max(20, watermarkSize * 0.3)}px` }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="bvw-preview-info">
                                    {videoContents[activeVideoIndex]?.title}
                                </div>
                            </div>

                            {/* Sağ Panel - Ayarlar */}
                            <div className="bvw-settings-panel">
                                {/* Logo Seçimi */}
                                <div className="bvw-section">
                                    <div className="bvw-section-header">
                                        <span>🖼️</span>
                                        <span>Logo</span>
                                    </div>

                                    <input
                                        ref={iconInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleIconSelect}
                                        id="bvw-logo-input"
                                        hidden
                                    />

                                    {!watermarkPreview ? (
                                        <label htmlFor="bvw-logo-input" className="bvw-logo-dropzone">
                                            <div className="bvw-dropzone-icon">+</div>
                                            <span>Logo Seç</span>
                                        </label>
                                    ) : (
                                        <div className="bvw-logo-preview">
                                            <img src={watermarkPreview} alt="Logo" />
                                            <button className="bvw-logo-remove" onClick={handleRemoveIcon}>✕</button>
                                        </div>
                                    )}
                                </div>

                                {/* Kayıtlı Markalar */}
                                {brands.length > 0 && (
                                    <div className="bvw-section">
                                        <div className="bvw-section-header">
                                            <span>🏷️</span>
                                            <span>Kayıtlı Markalar</span>
                                        </div>

                                        <div className="bvw-brands-list">
                                            {loadingBrands ? (
                                                <div className="bvw-brands-loading">Yükleniyor...</div>
                                            ) : (
                                                brands.map(brand => (
                                                    <div key={brand.id} className="bvw-brand-item">
                                                        <span className="bvw-brand-name">{brand.name}</span>
                                                        <div className="bvw-brand-logos">
                                                            {brand.logo_dark_url && (
                                                                <button
                                                                    className={`bvw-brand-logo-btn dark ${selectedBrandLogo?.brandId === brand.id &&
                                                                            selectedBrandLogo?.type === 'dark' ? 'selected' : ''
                                                                        }`}
                                                                    onClick={() => handleBrandLogoSelect(brand, 'dark')}
                                                                    title="Koyu arka plan logosu"
                                                                >
                                                                    <img
                                                                        src={`${BASE_URL}${brand.logo_dark_url}`}
                                                                        alt={`${brand.name} - Koyu`}
                                                                    />
                                                                </button>
                                                            )}
                                                            {brand.logo_light_url && (
                                                                <button
                                                                    className={`bvw-brand-logo-btn light ${selectedBrandLogo?.brandId === brand.id &&
                                                                            selectedBrandLogo?.type === 'light' ? 'selected' : ''
                                                                        }`}
                                                                    onClick={() => handleBrandLogoSelect(brand, 'light')}
                                                                    title="Açık arka plan logosu"
                                                                >
                                                                    <img
                                                                        src={`${BASE_URL}${brand.logo_light_url}`}
                                                                        alt={`${brand.name} - Açık`}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}

                                {watermarkPreview && (
                                    <>
                                        {/* Pozisyon */}
                                        <div className="bvw-section">
                                            <div className="bvw-section-header">
                                                <span>📍</span>
                                                <span>Pozisyon</span>
                                                <span className="bvw-section-value">{currentPositionLabel}</span>
                                            </div>

                                            <div className="bvw-position-grid">
                                                {positions.map(pos => (
                                                    <button
                                                        key={pos.key}
                                                        className={`bvw-position-btn ${watermarkPosition === pos.key ? 'active' : ''}`}
                                                        onClick={() => setWatermarkPosition(pos.key)}
                                                        title={pos.label}
                                                    >
                                                        <span className="bvw-position-dot"></span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Boyut */}
                                        <div className="bvw-section">
                                            <div className="bvw-section-header">
                                                <span>📏</span>
                                                <span>Boyut</span>
                                                <span className="bvw-section-value">{watermarkSize}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="30"
                                                max="200"
                                                value={watermarkSize}
                                                onChange={(e) => setWatermarkSize(Number(e.target.value))}
                                                className="bvw-slider"
                                            />
                                        </div>

                                        {/* Opaklık */}
                                        <div className="bvw-section">
                                            <div className="bvw-section-header">
                                                <span>💎</span>
                                                <span>Opaklık</span>
                                                <span className="bvw-section-value">{watermarkOpacity}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="10"
                                                max="100"
                                                value={watermarkOpacity}
                                                onChange={(e) => setWatermarkOpacity(Number(e.target.value))}
                                                className="bvw-slider"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    ) : (
                        /* İşlem Durumu */
                        <div className="bvw-processing-panel">
                            <div className="bvw-processing-header">
                                <div className="bvw-processing-spinner"></div>
                                <h3>İşleniyor...</h3>
                                <p>{processProgress.current} / {processProgress.total} video</p>
                            </div>

                            <div className="bvw-progress-bar">
                                <div
                                    className="bvw-progress-fill"
                                    style={{ width: `${(processProgress.current / processProgress.total) * 100}%` }}
                                ></div>
                            </div>

                            <div className="bvw-current-item">
                                İşlenen: <strong>{processProgress.currentTitle}</strong>
                            </div>

                            <div className="bvw-results-list">
                                {processResults.map((result, index) => (
                                    <div
                                        key={result.id}
                                        className={`bvw-result-item ${result.success ? 'success' : 'error'}`}
                                    >
                                        <span className="bvw-result-icon">
                                            {result.success ? '✓' : '✕'}
                                        </span>
                                        <span className="bvw-result-title">{result.title}</span>
                                        <span className="bvw-result-message">{result.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!isProcessing && (
                    <div className="bvw-modal-footer">
                        <button className="bvw-btn bvw-btn-secondary" onClick={onClose}>
                            İptal
                        </button>
                        <button
                            className="bvw-btn bvw-btn-primary"
                            onClick={handleStartBulkProcess}
                            disabled={!watermarkPreview || videoContents.length === 0}
                        >
                            <span>🎬</span>
                            {videoContents.length} Videoya Uygula
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BulkVideoWatermarkModal;